import PropTypes from 'prop-types';
import React from 'react';
import Dialog from './Dialog';
import { translate } from '../../../common/i18n';
import { loginAutotraderSite, registerAutotraderSite } from '../../../common/autotrader-session';

const tabBtnStyle = active => ({
    flex        : 1,
    padding     : '10px 0',
    border      : 'none',
    borderBottom: active ? '2px solid #c4f24a' : '2px solid transparent',
    background  : 'transparent',
    color       : active ? '#c4f24a' : '#8b9590',
    cursor      : 'pointer',
    fontSize    : '15px',
    fontWeight  : active ? 'bold' : 'normal',
    transition  : 'color 0.2s, border-bottom 0.2s',
});

const inputStyle = {
    width       : '100%',
    boxSizing   : 'border-box',
    marginTop   : '4px',
    padding     : '8px',
    background  : 'rgba(255, 255, 255, 0.04)',
    border      : '1px solid rgba(255, 255, 255, 0.08)',
    color       : '#eef2ef',
    borderRadius: '8px',
};

const Save = React.memo(({ closeDialog }) => {
    const [activeTab, setActiveTab] = React.useState('login');
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [error, setError] = React.useState('');
    const [busy, setBusy] = React.useState(false);

    const switchTab = tab => {
        setActiveTab(tab);
        setError('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
    };

    const handleLogin = async e => {
        e.preventDefault();
        setError('');
        if (!email || !password) {
            setError(String(translate('Please enter email and password.')));
            return;
        }
        setBusy(true);
        try {
            await loginAutotraderSite(email.trim(), password);
            window.dispatchEvent(new Event('site-login-success'));
            closeDialog();
        } catch (err) {
            setError(err.message || String(translate('Login failed.')));
        } finally {
            setBusy(false);
        }
    };

    const handleRegister = async e => {
        e.preventDefault();
        setError('');
        if (!email || !password || !confirmPassword) {
            setError(String(translate('Please fill in all fields.')));
            return;
        }
        if (password !== confirmPassword) {
            setError(String(translate('Passwords do not match.')));
            return;
        }
        setBusy(true);
        try {
            await registerAutotraderSite(email.trim(), password);
            window.dispatchEvent(new Event('site-login-success'));
            closeDialog();
        } catch (err) {
            setError(err.message || String(translate('Registration failed.')));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={{ padding: '24px 28px', maxWidth: '420px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                <a href="https://track.deriv.com/_Z0r0AQ1tTmp8zg8lBvFoLmNd7ZgqdRLk/14/" target="_blank" rel="noopener noreferrer" style={{ outline: 'none' }}>
                    <img src="/image/tradingbots.svg" style={{ maxWidth: '220px', display: 'block', margin: '0 auto' }} alt="Trading Bots logo" />
                </a>
            </div>
            <h1 style={{ textAlign: 'center', color: 'white', margin: '0 0 4px', fontSize: '1.6em' }}>{translate('Trade and profit with your Deriv Account')}</h1>
            <p style={{ textAlign: 'center', color: '#aaaaaa', fontSize: '13px', margin: '0 0 20px' }}>{translate('Use the blocks and trade contracts to earn money')}</p>

            <div style={{ display: 'flex', borderBottom: '1px solid #414141', marginBottom: '20px' }}>
                <button style={tabBtnStyle(activeTab === 'login')} onClick={() => switchTab('login')}>
                    {translate('Login')}
                </button>
                <button style={tabBtnStyle(activeTab === 'register')} onClick={() => switchTab('register')}>
                    {translate('Register')}
                </button>
            </div>

            {activeTab === 'login' && (
                <div>
                    <form onSubmit={handleLogin}>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ color: '#cccccc', fontSize: '13px', display: 'block' }}>
                                {translate('Email')}
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} autoComplete="username" />
                            </label>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ color: '#cccccc', fontSize: '13px', display: 'block' }}>
                                {translate('Password')}
                                <input type="password" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} autoComplete="current-password" />
                            </label>
                        </div>
                        {error && <p style={{ color: '#ef5350', fontSize: '13px', margin: '0 0 10px' }}>{error}</p>}
                        <button
                            type="submit"
                            disabled={busy}
                            style={{
                                width       : '100%',
                                padding     : '10px',
                                borderRadius: '4px',
                                border      : 'none',
                                background  : '#c4f24a',
                                color       : '#0a0d0b',
                                cursor      : busy ? 'not-allowed' : 'pointer',
                                fontWeight  : 'bold',
                            }}
                        >
                            {busy ? translate('Signing in...') : translate('Sign in')}
                        </button>
                    </form>
                </div>
            )}

            {activeTab === 'register' && (
                <div>
                    <button
                        style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.04)',
                            color          : '#eef2ef',
                            width          : '100%',
                            marginBottom   : '16px',
                            padding        : '10px',
                            borderRadius   : '4px',
                            border         : 'none',
                            cursor         : 'pointer',
                            fontWeight     : 'bold',
                        }}
                        onClick={() => {
                            window.open('https://track.deriv.com/_Z0r0AQ1tTmoKqFKZ7JdnQ2Nd7ZgqdRLk/46/', '_blank');
                        }}
                    >
                        {translate('Create a Deriv account')}
                    </button>
                    <p style={{ textAlign: 'center', color: '#aaaaaa', fontSize: '13px', margin: '0 0 16px' }}>{translate('or create a site account')}</p>
                    <form onSubmit={handleRegister}>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ color: '#cccccc', fontSize: '13px', display: 'block' }}>
                                {translate('Email')}
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} autoComplete="username" />
                            </label>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ color: '#cccccc', fontSize: '13px', display: 'block' }}>
                                {translate('Password')}
                                <input type="password" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} autoComplete="new-password" />
                            </label>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ color: '#cccccc', fontSize: '13px', display: 'block' }}>
                                {translate('Confirm Password')}
                                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={inputStyle} autoComplete="new-password" />
                            </label>
                        </div>
                        {error && <p style={{ color: '#ef5350', fontSize: '13px', margin: '0 0 10px' }}>{error}</p>}
                        <button
                            type="submit"
                            disabled={busy}
                            style={{
                                width       : '100%',
                                padding     : '10px',
                                borderRadius: '4px',
                                border      : 'none',
                                background  : '#c4f24a',
                                color       : '#0a0d0b',
                                cursor      : busy ? 'not-allowed' : 'pointer',
                                fontWeight  : 'bold',
                            }}
                        >
                            {busy ? translate('Registering...') : translate('Register')}
                        </button>
                    </form>
                </div>
            )}

            <p style={{ textAlign: 'center', fontSize: '10px', paddingTop: '16px', color: '#777777' }}>{translate('Deriv offers complex derivatives, such as options and contracts for difference ("CFDs"). These products may not be suitable for all clients, and trading them puts you at risk. Please make sure that you understand the following risks before trading: a) you may lose some or all of the money you invest, b) exchange rates will affect your profit and loss. You should never trade with borrowed money or with money that you cannot afford to lose.')}</p>
        </div>
    );
});

Save.propTypes = {
    closeDialog: PropTypes.func.isRequired,
};

export default class SaveDialog extends Dialog {
    constructor() {
        const closeDialog = () => {
            this.close();
        };
        const onSave = arg => {
            this.limitsPromise(arg);
            closeDialog();
        };
        super('welcome-login-dialog', '', <Save onSave={onSave} closeDialog={closeDialog} />, {
            height   : 'auto',
            width    : '50em',
            resizable: false,
            modal    : true,
            create(event, ui) {
                $(this)
                    .prev('.ui-dialog-titlebar')
                    .css({ display: 'none' });
            },
            open() {
                $('.ui-widget-overlay').css({
                    background: 'rgba(0, 0, 0, 0.85)',
                    opacity   : 1,
                });
            },
        });
        this.registerCloseOnOtherDialog();
    }

    save() {
        this.open();
        return new Promise(resolve => {
            this.limitsPromise = resolve;
        });
    }
}
